/**
 * Layout for the printable habit sheet: habits down the side, the days of one month
 * across, a ruled cell for every day and a total at the end of each row.
 *
 * Positions are millimetres on a page from `pageGeometry`, so one user unit prints as one
 * millimetre.
 */

import { type PageGeometry, r2 } from "@/lib/tool-kit/printables/page";
import type { PlacedText, Rect } from "@/lib/tool-kit/printables/reward-chart";
import {
  type FontSpec,
  fitSize,
  type TextMeasure,
  truncateToFit,
} from "@/lib/tool-kit/printables/text";

const BOLD: FontSpec = { kind: "sans", bold: true };
const SANS: FontSpec = { kind: "sans" };

export interface HabitSheetInput {
  title: string;
  monthLabel: string;
  /** Days in the month. */
  dayCount: number;
  /** Weekday of the 1st: 0 = Sunday … 6 = Saturday. */
  firstWeekday: number;
  /** Seven narrow weekday names, Sunday first. */
  weekdayInitials: readonly string[];
  /** One name per row; an empty string is a blank row to write in by hand. */
  habits: readonly string[];
  /** Ticks per row and day (index 0 = the 1st), or null for an empty sheet. */
  ticks: readonly (readonly boolean[])[] | null;
  nameHeading: string;
  totalHeading: string;
}

export interface HabitSheetLayout {
  title: PlacedText;
  month: PlacedText;
  table: Rect;
  /** Weekend columns, shaded. */
  shaded: Rect[];
  /** y of each horizontal rule, from the top of the table to its bottom. */
  hRules: number[];
  /** x of each vertical rule, from the left edge to the right edge. */
  vRules: number[];
  headings: PlacedText[];
  names: PlacedText[];
  /** Centres of ticked cells, and the size of the tick drawn in them. */
  ticks: { cx: number; cy: number }[];
  tickSize: number;
  totals: PlacedText[];
  rowHeight: number;
  /** True when the rows had to be squeezed below a comfortable writing height. */
  cramped: boolean;
}

export const NAME_COLUMN_MM = 52;
export const TOTAL_COLUMN_MM = 14;
export const HEADER_ROW_MM = 11;
const MAX_ROW_MM = 11;
const COMFORTABLE_ROW_MM = 7;
const TITLE_BLOCK_MM = 16;

function placed(
  text: string,
  x: number,
  y: number,
  size: number,
  anchor: PlacedText["anchor"] = "start",
  bold = false
): PlacedText {
  return { text, x: r2(x), y: r2(y), size: r2(size), anchor, bold };
}

/** Lays out the sheet. Rows beyond what fits are squeezed, never dropped. */
export function layoutHabitSheet(
  geometry: PageGeometry,
  measure: TextMeasure,
  input: HabitSheetInput
): HabitSheetLayout {
  const left = geometry.left;
  const right = geometry.left + geometry.contentWidth;
  const rows = Math.max(1, input.habits.length);
  const tableTop = geometry.top + TITLE_BLOCK_MM;
  const available = geometry.contentHeight - TITLE_BLOCK_MM - HEADER_ROW_MM;
  const rowHeight = Math.min(MAX_ROW_MM, available / rows);
  const dayWidth =
    (geometry.contentWidth - NAME_COLUMN_MM - TOTAL_COLUMN_MM) / input.dayCount;
  const daysLeft = left + NAME_COLUMN_MM;
  const totalLeft = daysLeft + dayWidth * input.dayCount;
  const tableBottom = tableTop + HEADER_ROW_MM + rowHeight * rows;

  const titleSize = fitSize(
    measure,
    input.title,
    BOLD,
    geometry.contentWidth * 0.62,
    9,
    4
  );
  const title = placed(
    input.title,
    left,
    geometry.top + 8,
    titleSize,
    "start",
    true
  );
  const month = placed(
    input.monthLabel,
    right,
    geometry.top + 8,
    6,
    "end",
    true
  );

  const vRules = [r2(left), r2(daysLeft)];
  for (let day = 1; day <= input.dayCount; day += 1) {
    vRules.push(r2(daysLeft + dayWidth * day));
  }
  vRules.push(r2(totalLeft + TOTAL_COLUMN_MM));
  const hRules = [r2(tableTop), r2(tableTop + HEADER_ROW_MM)];
  for (let row = 1; row <= rows; row += 1) {
    hRules.push(r2(tableTop + HEADER_ROW_MM + rowHeight * row));
  }

  const shaded: Rect[] = [];
  const headings: PlacedText[] = [
    placed(input.nameHeading, left + 2, tableTop + 7, 3.4, "start", true),
    placed(
      input.totalHeading,
      totalLeft + TOTAL_COLUMN_MM / 2,
      tableTop + 7,
      3.2,
      "middle",
      true
    ),
  ];
  const numberSize = Math.min(3.4, dayWidth * 0.62);
  for (let index = 0; index < input.dayCount; index += 1) {
    const weekday = (input.firstWeekday + index) % 7;
    const x = daysLeft + dayWidth * index;
    if (weekday === 0 || weekday === 6) {
      shaded.push({
        x: r2(x),
        y: r2(tableTop),
        width: r2(dayWidth),
        height: r2(tableBottom - tableTop),
      });
    }
    headings.push(
      placed(
        input.weekdayInitials[weekday] ?? "",
        x + dayWidth / 2,
        tableTop + 4.2,
        numberSize * 0.82,
        "middle"
      ),
      placed(
        String(index + 1),
        x + dayWidth / 2,
        tableTop + 8.6,
        numberSize,
        "middle",
        true
      )
    );
  }

  const names: PlacedText[] = [];
  const totals: PlacedText[] = [];
  const ticks: { cx: number; cy: number }[] = [];
  const nameSize = Math.min(4, Math.max(2.6, rowHeight * 0.5));
  input.habits.forEach((name, row) => {
    const rowTop = tableTop + HEADER_ROW_MM + rowHeight * row;
    const baseline = rowTop + rowHeight / 2 + nameSize * 0.35;
    if (name.trim()) {
      names.push(
        placed(
          truncateToFit(
            measure,
            name.trim(),
            SANS,
            nameSize,
            NAME_COLUMN_MM - 4
          ),
          left + 2,
          baseline,
          nameSize
        )
      );
    }
    const rowTicks = input.ticks?.[row];
    if (!rowTicks) {
      return;
    }
    let count = 0;
    for (let index = 0; index < input.dayCount; index += 1) {
      if (rowTicks[index]) {
        count += 1;
        ticks.push({
          cx: r2(daysLeft + dayWidth * (index + 0.5)),
          cy: r2(rowTop + rowHeight / 2),
        });
      }
    }
    totals.push(
      placed(
        String(count),
        totalLeft + TOTAL_COLUMN_MM / 2,
        baseline,
        nameSize,
        "middle",
        true
      )
    );
  });

  return {
    title,
    month,
    table: {
      x: r2(left),
      y: r2(tableTop),
      width: r2(right - left),
      height: r2(tableBottom - tableTop),
    },
    shaded,
    hRules,
    vRules,
    headings,
    names,
    ticks,
    tickSize: r2(Math.min(dayWidth, rowHeight) * 0.55),
    totals,
    rowHeight: r2(rowHeight),
    cramped: rowHeight < COMFORTABLE_ROW_MM,
  };
}
